import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Text,
  VStack,
  Grid,
  Image,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  Heading,
  Card,
  CardBody,
  Link,
  useToast,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink
} from '@chakra-ui/react';
import { DownloadIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { getPurchasedImages } from '../lib/galleryService';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

interface PurchasedImage {
  id: string;
  filename: string;
  originalUrl?: string;
  previewUrl: string;
  downloadUrl?: string;
}

const DownloadPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [images, setImages] = useState<PurchasedImage[]>([]);
  const [galleryTitle, setGalleryTitle] = useState<string>('');
  const [galleryToken, setGalleryToken] = useState<string>('');
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [daysUntilExpiry, setDaysUntilExpiry] = useState<number | undefined>();
  const [expiresAt, setExpiresAt] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  const [creatingZip, setCreatingZip] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (token) {
      loadPurchasedImages(token);
    }
  }, [token]);

  const loadPurchasedImages = async (downloadToken: string) => {
    try {
      const result = await getPurchasedImages(downloadToken);
      
      if (result.success && result.images) {
        setImages(result.images);
        setGalleryTitle(result.galleryTitle || 'Your Images');
        setGalleryToken(result.galleryToken || '');
        setIsExpired(result.isExpired || false);
        setDaysUntilExpiry(result.daysUntilExpiry);
        setExpiresAt(result.expiresAt);
      } else if (result.isExpired) {
        // Handle expired gallery
        setIsExpired(true);
        setGalleryTitle(result.galleryTitle || 'Gallery');
        setGalleryToken(result.galleryToken || '');
        setExpiresAt(result.expiresAt);
        setError('Gallery has expired');
      } else {
        setError(result.error || 'Download link not found or expired');
      }
    } catch (err) {
      setError('Failed to load your images');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (image: PurchasedImage) => {
    const downloadUrl = image.downloadUrl || image.originalUrl || image.previewUrl;
    
    setDownloadingIds(prev => new Set(prev.add(image.id)));

    try {
      // Create a temporary link to trigger download
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = image.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Download started',
        description: `${image.filename} is downloading`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Download failed',
        description: 'Failed to download image. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(image.id);
        return newSet;
      });
    }
  };

  const downloadAllAsZip = async () => {
    if (images.length === 0) return;

    setCreatingZip(true);
    
    try {
      const zip = new JSZip();
      const folder = zip.folder(galleryTitle || 'Gallery');
      
      toast({
        title: 'Creating zip file...',
        description: `Downloading ${images.length} images`,
        status: 'info',
        duration: 3000,
      });

      // Download all images and add to zip
      const downloadPromises = images.map(async (image, index) => {
        try {
          const downloadUrl = image.downloadUrl || image.originalUrl || image.previewUrl;
          const response = await fetch(downloadUrl);
          if (!response.ok) throw new Error(`Failed to fetch ${image.filename}`);
          
          const blob = await response.blob();
          const filename = image.filename || `image_${index + 1}.jpg`;
          
          folder?.file(filename, blob);
          return { success: true, filename };
        } catch (error) {
          console.error(`Error downloading ${image.filename}:`, error);
          return { success: false, filename: image.filename, error };
        }
      });

      const results = await Promise.all(downloadPromises);
      const successCount = results.filter(r => r.success).length;
      const failedCount = results.length - successCount;

      if (successCount === 0) {
        throw new Error('Failed to download any images');
      }

      // Generate and save zip
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipName = `${galleryTitle || 'Gallery'}.zip`;
      saveAs(zipBlob, zipName);

      let toastMessage = `Downloaded ${successCount} images as zip file`;
      if (failedCount > 0) {
        toastMessage += ` (${failedCount} failed)`;
      }

      toast({
        title: 'Download complete!',
        description: toastMessage,
        status: failedCount > 0 ? 'warning' : 'success',
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error creating zip:', error);
      toast({
        title: 'Download failed',
        description: error instanceof Error ? error.message : 'Failed to create zip file',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreatingZip(false);
    }
  };

  const downloadAllImages = downloadAllAsZip;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="50vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    // Special handling for expired galleries
    if (isExpired && expiresAt) {
      return (
        <Box maxW="container.md" mx="auto" p={8}>
          <VStack spacing={6} align="center">
            <Alert status="error">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Gallery has expired</Text>
                <Text fontSize="sm">
                  This gallery expired on {new Date(expiresAt).toLocaleDateString('en-GB', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Text>
              </Box>
            </Alert>
            
            <Card maxW="md" w="100%">
              <CardBody textAlign="center">
                <VStack spacing={4}>
                  <Heading size="md">{galleryTitle}</Heading>
                  <Text color="gray.600">
                    Unfortunately, this download link has expired and the images are no longer available for download.
                  </Text>
                  <Text fontWeight="bold" color="blue.600">
                    Contact me for more information:
                  </Text>
                  <Link href="mailto:algranoconpablo@gmail.com" color="blue.500" fontWeight="bold">
                    algranoconpablo@gmail.com
                  </Link>
                  <Text fontSize="sm" color="gray.500">
                    I may be able to provide alternative access to your images.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
            
            {galleryToken && (
              <Link href={`/client/${galleryToken}`} color="blue.500">
                ← Back to gallery
              </Link>
            )}
          </VStack>
        </Box>
      );
    }

    // Regular error handling
    return (
      <Box maxW="container.md" mx="auto" p={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
        <Box mt={4}>
          <Link href="/" color="blue.500">
            ← Back to main site
          </Link>
        </Box>
      </Box>
    );
  }

  return (
    <Box maxW="container.xl" mx="auto" p={8}>
      <VStack spacing={8} align="stretch">
        {/* Breadcrumb Navigation */}
        {galleryToken && (
          <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.500" />}>
            <BreadcrumbItem>
              <BreadcrumbLink href={`/client/${galleryToken}`} color="blue.500">
                Gallery
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink color="gray.500">
                Download
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
        )}

        <Box textAlign="center">
          <Heading size="lg" mb={2}>
            {galleryTitle}
          </Heading>
          <Text fontSize="lg" color="gray.600" mb={4}>
            Your images are ready for download
          </Text>
          
          {/* Expiration notice */}
          {expiresAt && daysUntilExpiry !== undefined && (
            <Box 
              bg={daysUntilExpiry <= 3 ? "red.50" : daysUntilExpiry <= 7 ? "orange.50" : "blue.50"}
              border="1px solid" 
              borderColor={daysUntilExpiry <= 3 ? "red.200" : daysUntilExpiry <= 7 ? "orange.200" : "blue.200"}
              borderRadius="lg" 
              p={3} 
              mb={4}
              maxW="500px"
              mx="auto"
            >
              <Text 
                fontSize="md" 
                color={daysUntilExpiry <= 3 ? "red.800" : daysUntilExpiry <= 7 ? "orange.800" : "blue.800"}
                fontWeight="medium"
              >
                {daysUntilExpiry <= 0 
                  ? "⏰ Download access has expired!"
                  : daysUntilExpiry === 1
                  ? "⏰ Download access expires today!"
                  : daysUntilExpiry <= 3
                  ? `⏰ Download access expires in ${daysUntilExpiry} days`
                  : daysUntilExpiry <= 7
                  ? `⏰ Download access expires in ${daysUntilExpiry} days`
                  : `Download access expires in ${daysUntilExpiry} days`
                }
              </Text>
              <Text 
                fontSize="xs" 
                color={daysUntilExpiry <= 3 ? "red.600" : daysUntilExpiry <= 7 ? "orange.600" : "blue.600"}
                mt={1}
              >
                Expires on {new Date(expiresAt).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Text>
            </Box>
          )}
          
          {images.length > 1 && (
            <Button 
              onClick={downloadAllImages}
              colorScheme="blue" 
              size="lg"
              leftIcon={<DownloadIcon />}
              mb={6}
              isLoading={creatingZip}
              loadingText="Creating zip file..."
            >
              Download All as Zip
            </Button>
          )}
        </Box>

        <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={6}>
          {images.map(image => (
            <Card key={image.id}>
              <CardBody>
                <VStack spacing={4}>
                  <Image
                    src={image.previewUrl}
                    alt={image.filename}
                    w="100%"
                    maxH="300px"
                    objectFit="contain"
                    bg="gray.50"
                    borderRadius="md"
                  />
                  
                  <VStack spacing={2} w="100%">
                    <Text fontSize="sm" fontWeight="bold" textAlign="center">
                      {image.filename}
                    </Text>
                    
                    <Button
                      onClick={() => handleDownload(image)}
                      colorScheme="green"
                      size="sm"
                      leftIcon={<DownloadIcon />}
                      w="100%"
                      isLoading={downloadingIds.has(image.id)}
                      loadingText="Downloading..."
                    >
                      Download
                    </Button>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </Grid>

      </VStack>
    </Box>
  );
};

export default DownloadPage;